import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import SectionLabel from '@/components/base/SectionLabel';
import { products, phoneHref, whatsappHref, type Product } from '../data';

function ProductModal({ product, onClose }: { product: Product; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/60 p-5 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`${product.name} details`}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-foreground-800 shadow-sm transition-colors hover:text-primary-600"
          aria-label="Close"
        >
          <i className="ri-close-line text-xl" aria-hidden="true" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="h-72 md:h-auto">
            <img
              src={product.image}
              alt={`${product.name} from SRM Textiles`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="p-7 md:p-10 lg:p-12">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-500">{product.category}</p>
            <h3 className="mt-3 font-heading text-3xl font-semibold text-foreground-950">{product.name}</h3>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground-700">{product.description}</p>

            <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-background-200 pt-6">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-foreground-500">Indicative Price</p>
                <p className="mt-1.5 text-lg font-semibold text-foreground-950">{product.price}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-foreground-500">Fabric</p>
                <p className="mt-1.5 text-sm text-foreground-800">{product.fabric}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-foreground-500">Occasion</p>
                <p className="mt-1.5 text-sm text-foreground-800">{product.occasion}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-foreground-500">Colours</p>
                <p className="mt-1.5 text-sm text-foreground-800">{product.colours.join(', ')}</p>
              </div>
            </div>

            <p className="mt-6 text-[12px] text-foreground-500">
              Indicative price shown. Final price may vary depending on fabric, design and availability.
            </p>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href={phoneHref}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-phone-line" aria-hidden="true" />
                Enquire About This Style
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-5 py-3.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300"
              >
                <i className="ri-whatsapp-line" aria-hidden="true" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductShowcase() {
  const [active, setActive] = useState<Product | null>(null);

  return (
    <section id="sarees" className="bg-background-50 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal className="max-w-2xl">
          <SectionLabel>A Glimpse of Our Collection</SectionLabel>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl leading-[1.12] font-medium text-foreground-950">
            Sarees, Dhotis &amp; Traditional Wear
          </h2>
          <p className="mt-5 text-[15px] md:text-base leading-relaxed text-foreground-700">
            Explore a selection of traditional Indian styles available through SRM Textiles.
            Prices shown are indicative and may vary depending on fabric, design and availability.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 gap-y-12">
          {products.map((product, i) => (
            <Reveal key={product.id} delay={(i % 4) * 80}>
              <article className="group flex h-full flex-col">
                <button
                  type="button"
                  onClick={() => setActive(product)}
                  className="img-zoom rounded-lg overflow-hidden text-left"
                  aria-label={`View ${product.name}`}
                >
                  <img
                    src={product.image}
                    alt={`${product.name} — traditional Indian textile from SRM Textiles`}
                    className="h-80 w-full object-cover object-top"
                  />
                </button>
                <div className="mt-5 flex flex-1 flex-col">
                  <h3 className="font-heading text-xl font-semibold text-foreground-950">{product.name}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">{product.description}</p>
                  <p className="mt-4 flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-foreground-950">{product.price}</span>
                    <span className="text-[11px] uppercase tracking-wide text-foreground-500">Indicative</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => setActive(product)}
                    className="mt-5 inline-flex items-center gap-2 self-start whitespace-nowrap text-[13px] font-semibold text-primary-600 transition-colors hover:text-primary-500"
                  >
                    Enquire About This Style
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      {active && <ProductModal product={active} onClose={() => setActive(null)} />}
    </section>
  );
}